import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Bot,
  Calendar,
  Check,
  LayoutGrid,
  Paperclip,
  Smartphone,
  Sparkles,
  Workflow,
} from 'lucide-react';

import { BillingCycleToggle } from '../components/BillingCycleToggle';
import { CurrencySelector } from '../components/CurrencySelector';
import { PublicFooter } from '../components/PublicFooter';
import { Seo } from '../components/Seo';
import { ThemeToggle } from '../components/ThemeToggle';
import { trackCta } from '../lib/analytics';
import { formatStorageMb } from '../lib/catalog';
import { formatPrice, useCurrency } from '../lib/currency';
import { effectivePrice, usePricingDiscounts, type BillingCycle } from '../lib/pricing';
import { DiscountBadge, PriceWithDiscount } from '../components/PriceWithDiscount';
import { getLocale, t, type Locale } from '../lib/i18n';
import { setJsonLd, usePublicSeo } from '../lib/seo';
import { usePublicLocale } from '../lib/usePublicLocale';
import { useReveal } from '../lib/useReveal';

/**
 * Landing pública - home del sitio (des.evoluado.com/tembiapo/).
 *
 * Se muestra a visitantes SIN sesión (y mientras se bootea la sesión). Un
 * usuario autenticado que cae en "/" es redirigido a su dashboard por RootRoute,
 * así que esta página no asume nada de auth.
 *
 * DESIGN READ: landing B2B SaaS para equipos/pymes de Paraguay y Brasil, tono
 * cálido "sin complicaciones", marca teal + acento sol. Lenguaje moderno y
 * animado pero no experimental → dials ≈ VARIANCE 6 / MOTION 5 / DENSITY 4.
 * Es un rediseño-overhaul que PRESERVA marca, IA (Hero→prueba→Features→Planes→
 * Apps→CTA), copy e i18n, y las anclas #features/#plans/#apps.
 *
 * Reglas aplicadas (design-taste-frontend): hero acotado a 4 elementos de texto
 * (sin micro-strips ni tagline bajo los CTAs), stats reales debajo del hero,
 * ≤2 eyebrows en toda la página, un solo label por intención de CTA ("Probar
 * gratis" para registro), acento único (teal; sol con cuentagotas), movimiento
 * motivado y con paridad reduced-motion, sin scroll listeners (Intersection
 * Observer), sin dots decorativos. El mini-tablero es un preview REAL en
 * miniatura del Kanban del producto (no un screenshot falso). Colores 100% desde
 * tokens de marca (claro/oscuro). Iconos lucide (dependencia ya existente).
 */
export function LandingPage() {
  const { locale, switchLocale } = usePublicLocale();
  useLandingJsonLd();

  return (
    <div className="min-h-full bg-bg text-ink">
      {/* SEO de la home: usa los defaults del idioma (sin title propio). */}
      <Seo type="website" />
      <LandingHeader locale={locale} onSwitchLocale={switchLocale} />
      <main id="top">
        <Hero />
        <StatsBand />
        <Features />
        <AiIntegration />
        <Plans />
        <Apps />
        <FinalCta />
      </main>
      <PublicFooter />
    </div>
  );
}

/**
 * Datos estructurados (JSON-LD) de la home: Organization + WebSite +
 * SoftwareApplication en un @graph. Usa la config del Super Admin (nombre/URL/
 * logo) y el idioma activo para la descripción. Se limpia al desmontar.
 */
function useLandingJsonLd() {
  const { data: config } = usePublicSeo();
  const locale = getLocale();

  useEffect(() => {
    if (!config) return;
    const name = config.site_name || 'Tembiapo';
    const url = config.canonical_base || 'https://tembiapoapp.com';
    const logo = config.og_image;
    const description = config.defaults[locale]?.description ?? '';

    setJsonLd('landing', {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name,
          url,
          logo,
          sameAs: [],
        },
        {
          '@type': 'WebSite',
          name,
          url,
        },
        {
          '@type': 'SoftwareApplication',
          name,
          description,
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web, iOS, Android',
        },
      ],
    });

    return () => setJsonLd('landing', null);
  }, [config, locale]);
}


/* ------------------------------------------------------------------ layout -- */

const WRAP = 'mx-auto w-full max-w-6xl px-6';

/** Ficha de la app publicada en Google Play. */
const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.evoluado.tembiapp';

/** Ficha de la app publicada en la App Store (id numérico de App Store Connect). */
const APP_STORE_URL = 'https://apps.apple.com/app/id6799162378';

/** Glifo del triángulo de Google Play (multicolor oficial), como SVG inline. */
function GooglePlayGlyph() {
  return (
    <svg width={18} height={18} viewBox="0 0 512 512" aria-hidden="true">
      <path fill="#00D3FF" d="M47 32C40 36 36 43 36 54v404c0 11 4 18 11 22l235-224L47 32z" />
      <path fill="#00F076" d="M47 32c6-4 14-3 23 2l268 152-56 54L47 32z" />
      <path fill="#FF3945" d="M282 256l56 54-268 152c-9 5-17 6-23 2l235-208z" />
      <path fill="#FFC800" d="M338 184l78 44c18 10 18 46 0 56l-78 44-56-54 56-90z" />
    </svg>
  );
}

/** Logo de Apple (monocromo, usa currentColor), como SVG inline. */
function AppleGlyph() {
  return (
    <svg width={17} height={17} viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function BrandMark({ className }: { className?: string }) {
  // Lockup completo (ícono + "tembiapo"); fondo transparente → sirve claro/oscuro.
  return (
    <img
      src={`${import.meta.env.BASE_URL}brand/logo-lockup.png`}
      alt="Tembiapo"
      className={className ?? 'h-8 w-auto'}
    />
  );
}

/** Wrapper de scroll-reveal: revela su contenido al entrar al viewport (una vez).
 *  Con prefers-reduced-motion las clases .reveal quedan siempre visibles (CSS). */
function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${shown ? 'reveal-in' : ''} ${className ?? ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ header -- */

function LandingHeader({
  locale,
  onSwitchLocale,
}: {
  locale: Locale;
  onSwitchLocale: (l: Locale) => void;
}) {
  return (
    <header className="sticky top-0 z-50 border-b border-line bg-bg/80 backdrop-blur-md">
      <div className={`${WRAP} flex h-16 items-center gap-5`}>
        <a href="#top" aria-label="Tembiapo" className="transition-transform hover:scale-[1.02]">
          <BrandMark className="h-8 w-auto" />
        </a>

        <nav className="ml-3 hidden items-center gap-6 text-sm font-medium text-muted md:flex">
          <a href="#features" className="transition-colors hover:text-ink">
            {t('landing.nav.product')}
          </a>
          <a href="#plans" className="transition-colors hover:text-ink">
            {t('landing.nav.plans')}
          </a>
          <a href="#apps" className="transition-colors hover:text-ink">
            {t('landing.nav.apps')}
          </a>
        </nav>

        <div className="ml-auto flex items-center gap-2.5">
          <div
            role="group"
            aria-label={t('landing.langLabel')}
            className="inline-flex overflow-hidden rounded-token border border-line font-mono text-xs"
          >
            {(['es', 'pt'] as const).map((l) => (
              <button
                key={l}
                type="button"
                aria-pressed={locale === l}
                onClick={() => onSwitchLocale(l)}
                className={
                  locale === l
                    ? 'bg-brand px-2.5 py-1.5 text-brand-ink'
                    : 'px-2.5 py-1.5 text-muted transition-colors hover:text-ink'
                }
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <ThemeToggle />

          <Link
            to="/login"
            className="hidden rounded-token border border-line px-4 py-2 text-sm font-medium text-ink transition-colors hover:border-brand hover:text-brand sm:inline-flex"
          >
            {t('landing.action.login')}
          </Link>
          <Link
            to="/registro"
            onClick={() => trackCta('register')}
            className="group inline-flex items-center gap-1.5 rounded-token bg-brand px-4 py-2 text-sm font-semibold text-brand-ink shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-2"
          >
            {t('landing.action.try')}
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------- hero -- */

function Hero() {
  return (
    <section className="relative overflow-hidden pb-16 pt-16 sm:pt-24">
      {/* Profundidad de fondo (decorativa, aria-hidden): halo radial teal a muy
          baja opacidad + patrón ñandutí estático. Sin loops innecesarios. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="landing-nanduti absolute -right-64 -top-72 h-[760px] w-[760px]" />
        <div
          className="absolute -left-40 top-6 h-[440px] w-[440px] rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle, color-mix(in srgb, var(--brand) 18%, transparent), transparent 70%)',
          }}
        />
      </div>

      <div className={`${WRAP} relative grid items-center gap-14 lg:grid-cols-[1.05fr_.95fr]`}>
        <div>
          <span
            className="hero-rise inline-flex items-center gap-2 rounded-full border border-line bg-surface/70 px-3 py-1 font-mono text-xs uppercase tracking-[0.16em] text-brand-2 backdrop-blur-sm"
            style={{ animationDelay: '0.02s' }}
          >
            <Sparkles size={13} className="text-brand-2" />
            {t('landing.hero.eyebrow')}
          </span>

          <h1
            className="hero-rise mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-[3.5rem]"
            style={{ animationDelay: '0.1s' }}
          >
            {t('landing.hero.titleBefore')}
            <span className="tb-highlight text-brand">{t('landing.hero.titleAccent')}</span>
            {t('landing.hero.titleAfter')}
          </h1>

          <p
            className="hero-rise mt-5 max-w-[40ch] text-lg text-muted"
            style={{ animationDelay: '0.2s' }}
          >
            {t('landing.hero.lede')}
          </p>

          <div
            className="hero-rise mt-8 flex flex-wrap items-center gap-3"
            style={{ animationDelay: '0.3s' }}
          >
            <Link
              to="/registro"
              onClick={() => trackCta('register')}
              className="group inline-flex items-center gap-2 rounded-token bg-brand px-5 py-3 font-display text-[15px] font-semibold text-brand-ink shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-2 hover:shadow-card-hover"
            >
              {t('landing.action.try')}
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="#features"
              className="inline-flex items-center gap-2 rounded-token border border-line px-5 py-3 font-display text-[15px] font-semibold text-ink transition-all hover:-translate-y-0.5 hover:border-brand-2 hover:text-brand-2"
            >
              {t('landing.action.how')}
            </a>
          </div>
        </div>

        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 rounded-[32px] blur-2xl"
            style={{
              background:
                'radial-gradient(60% 60% at 60% 40%, color-mix(in srgb, var(--brand) 16%, transparent), transparent 75%)',
            }}
          />
          <MiniBoard />
        </div>
      </div>
    </section>
  );
}

type MiniCard = {
  tag: 'mkt' | 'tech';
  textKey: Parameters<typeof t>[0];
  initials: string;
  pri: string;
};

function MiniCardChip({ tag }: { tag: 'mkt' | 'tech' }) {
  return (
    <span
      className={
        tag === 'tech'
          ? 'inline-block rounded-full bg-brand/15 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-brand-2'
          : 'inline-block rounded-full bg-sun/20 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-sun'
      }
    >
      {tag === 'tech' ? t('landing.board.tag.tech') : t('landing.board.tag.mkt')}
    </span>
  );
}

/**
 * Preview REAL en miniatura del Kanban del producto (no un screenshot falso):
 * misma estructura de tarjeta que el tablero real (chip de rubro, título,
 * avatar con iniciales, prioridad). Decorativo → aria-hidden. Vive con una leve
 * flotación y una tarjeta que "viaja" entre columnas (demuestra el drag & drop,
 * la interacción central del producto). Se congela con reduced-motion.
 */
function MiniBoard() {
  const columns: { titleKey: Parameters<typeof t>[0]; count: number; cards: MiniCard[] }[] = [
    {
      titleKey: 'landing.board.todo',
      count: 3,
      cards: [
        { tag: 'mkt', textKey: 'landing.board.card.landing', initials: 'RC', pri: `◔ ${t('task.priority.HIGH')}` },
        { tag: 'tech', textKey: 'landing.board.card.api', initials: 'JM', pri: `◑ ${t('task.priority.MEDIUM')}` },
      ],
    },
    {
      titleKey: 'landing.board.doing',
      count: 2,
      cards: [
        { tag: 'tech', textKey: 'landing.board.card.billing', initials: 'AL', pri: `● ${t('task.priority.URGENT')}` },
        { tag: 'mkt', textKey: 'landing.board.card.content', initials: 'SP', pri: `◑ ${t('task.priority.MEDIUM')}` },
      ],
    },
    {
      titleKey: 'landing.board.done',
      count: 4,
      cards: [
        { tag: 'tech', textKey: 'landing.board.card.setup', initials: 'JM', pri: '✓' },
        { tag: 'mkt', textKey: 'landing.board.card.brief', initials: 'RC', pri: '✓' },
      ],
    },
  ];

  return (
    <div
      aria-hidden
      className="tb-float-slow relative grid grid-cols-3 gap-2.5 rounded-[20px] border border-line bg-surface/95 p-4 shadow-card backdrop-blur-sm"
    >
      {/* Tarjeta "en vuelo": recorre las 3 columnas en loop suave (drag simulado).
          El ancho/left calzan una columna; el keyframe la posa en la siguiente. */}
      <div
        className="tb-travel absolute z-20 rounded-xl border border-brand-2/40 bg-surface-2 p-3 shadow-lift"
        style={{ left: '1rem', top: '2.9rem', width: 'calc((100% - 2rem - 1.25rem) / 3)' }}
      >
        <MiniCardChip tag="tech" />
        <p className="my-2 text-sm font-medium leading-tight text-ink">
          {t('landing.board.card.billing')}
        </p>
        <div className="flex items-center justify-between">
          <span className="grid h-6 w-6 place-items-center rounded-full bg-brand font-mono text-[10px] font-bold text-brand-ink">
            AL
          </span>
          <span className="font-mono text-[10px] text-danger">● {t('task.priority.URGENT')}</span>
        </div>
      </div>

      {columns.map((col) => (
        <div key={col.titleKey}>
          <h4 className="mb-3 mt-0.5 flex items-center justify-between font-mono text-[11px] uppercase tracking-wider text-muted">
            <span>{t(col.titleKey)}</span>
            <span className="grid h-4 min-w-4 place-items-center rounded-full bg-surface-2 px-1 text-[10px] text-muted">
              {col.count}
            </span>
          </h4>
          {col.cards.map((card, i) => (
            <div
              key={card.textKey}
              className="animate-rise mb-2.5 rounded-xl border border-line bg-surface-2 p-3"
              style={{ animationDelay: `${0.35 + i * 0.14}s` }}
            >
              <MiniCardChip tag={card.tag} />
              <p className="my-2 text-sm font-medium leading-tight text-ink">{t(card.textKey)}</p>
              <div className="flex items-center justify-between">
                <span className="grid h-6 w-6 place-items-center rounded-full bg-brand font-mono text-[10px] font-bold text-brand-ink">
                  {card.initials}
                </span>
                <span className="font-mono text-[10px] text-muted">{card.pri}</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------- stats band -- */

/** Banda de prueba justo debajo del hero: claim + 3 datos REALES del producto
 *  (4 verticales sembradas, 4 vistas de trabajo, trial de 30 días). Aloja lo
 *  que la skill saca del hero (micro-strips de confianza / trial). */
function StatsBand() {
  const stats = [
    { value: '6', label: t('landing.hero.stat.verticals') },
    { value: '4', label: t('landing.hero.stat.views') },
    { value: '30', label: t('landing.hero.stat.trial') },
  ];
  return (
    <div className="border-y border-line bg-surface/60">
      <Reveal
        className={`${WRAP} flex flex-col gap-6 py-7 sm:flex-row sm:items-center sm:justify-between`}
      >
        <p className="max-w-[24ch] font-display text-lg font-bold text-ink">
          {t('landing.strip.claim')}
        </p>
        <dl className="grid grid-cols-3 gap-6 sm:gap-10">
          {stats.map((s) => (
            <div key={s.label} className="text-center sm:text-left">
              <dt className="font-display text-3xl font-extrabold tabular-nums text-brand">
                {s.value}
              </dt>
              <dd className="mt-1 text-xs leading-tight text-muted">{s.label}</dd>
            </div>
          ))}
        </dl>
      </Reveal>
    </div>
  );
}

/* ------------------------------------------------------------- section head -- */

function SectionHead({
  eyebrow,
  title,
  subtitle,
  center,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  center?: boolean;
}) {
  return (
    <Reveal className={`mb-10 max-w-[56ch] ${center ? 'mx-auto text-center' : ''}`}>
      {eyebrow && (
        <span className="font-mono text-xs uppercase tracking-[0.18em] text-brand-2">{eyebrow}</span>
      )}
      <h2
        className={`font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl ${
          eyebrow ? 'mt-3' : ''
        }`}
      >
        {title}
      </h2>
      {subtitle && <p className="mt-3 text-lg text-muted">{subtitle}</p>}
    </Reveal>
  );
}

/* ---------------------------------------------------------------- features -- */

type Feature = {
  icon: ReactNode;
  title: Parameters<typeof t>[0];
  body: Parameters<typeof t>[0];
  /** Fondo tintado para dar diversidad visual a la grilla (no cards idénticas). */
  tint?: boolean;
};

function Features() {
  const feats: Feature[] = [
    { icon: <LayoutGrid size={20} />, title: 'landing.features.views.title', body: 'landing.features.views.body', tint: true },
    { icon: <Calendar size={20} />, title: 'landing.features.agenda.title', body: 'landing.features.agenda.body' },
    { icon: <Check size={20} />, title: 'landing.features.plans.title', body: 'landing.features.plans.body', tint: true },
    { icon: <Workflow size={20} />, title: 'landing.features.workflows.title', body: 'landing.features.workflows.body' },
    { icon: <Bell size={20} />, title: 'landing.features.notifications.title', body: 'landing.features.notifications.body', tint: true },
    { icon: <Paperclip size={20} />, title: 'landing.features.files.title', body: 'landing.features.files.body' },
    { icon: <Smartphone size={20} />, title: 'landing.features.mobile.title', body: 'landing.features.mobile.body', tint: true },
  ];
  return (
    <section id="features" className={`${WRAP} py-20`}>
      {/* Sin eyebrow: el titular alcanza (restricción de eyebrows de la skill). */}
      <SectionHead title={t('landing.features.title')} subtitle={t('landing.features.subtitle')} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {feats.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 90}>
            <div
              className="group h-full rounded-2xl border border-line p-6 transition-all duration-300 hover:-translate-y-1 hover:border-brand-2/60 hover:shadow-card-hover"
              style={
                f.tint
                  ? {
                      background:
                        'linear-gradient(150deg, color-mix(in srgb, var(--brand) 9%, var(--surface)), var(--surface) 62%)',
                    }
                  : { background: 'var(--surface)' }
              }
            >
              <div className="mb-3.5 grid h-11 w-11 place-items-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-brand-ink">
                {f.icon}
              </div>
              <h3 className="font-display text-lg font-bold text-ink">{t(f.title)}</h3>
              <p className="mt-2 text-sm text-muted">{t(f.body)}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- plans -- */

function PlanCard({
  name,
  priceNode,
  discountNode,
  features,
  cta,
  ctaTo,
  featured,
  badge,
}: {
  name: string;
  priceNode: ReactNode;
  discountNode?: ReactNode;
  features: string[];
  cta: string;
  ctaTo: string;
  featured?: boolean;
  badge?: string;
}) {
  return (
    <div
      className={`group relative flex h-full flex-col rounded-2xl border bg-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover ${
        featured ? 'border-brand-2 shadow-card' : 'border-line hover:border-brand-2/60'
      }`}
    >
      {featured && (
        <div
          aria-hidden
          className="tb-glow pointer-events-none absolute -inset-px -z-10 rounded-2xl blur-md"
          style={{
            background:
              'radial-gradient(80% 60% at 50% 0%, color-mix(in srgb, var(--brand) 30%, transparent), transparent 70%)',
          }}
        />
      )}
      {badge && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 font-mono text-[10px] uppercase tracking-wide text-brand-ink shadow-card">
          <Sparkles size={11} className="text-sun" />
          {badge}
        </span>
      )}
      <span className="font-mono text-xs uppercase tracking-[0.12em] text-brand-2">{name}</span>
      <div className="mt-3">{priceNode}</div>
      {discountNode && <div className="mt-2">{discountNode}</div>}
      <ul className="my-6 flex flex-col gap-2.5 text-sm text-muted">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0 text-brand-2" />
            {f}
          </li>
        ))}
      </ul>
      <Link
        to={ctaTo}
        className={`mt-auto inline-flex items-center justify-center gap-1.5 rounded-token px-4 py-2.5 font-display text-sm font-semibold transition-all ${
          featured
            ? 'bg-brand text-brand-ink hover:bg-brand-2'
            : 'border border-line text-ink hover:border-brand-2 hover:text-brand-2'
        }`}
      >
        {cta}
        <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

function Plans() {
  const perMonth = t('landing.plans.perMonth');
  // Precios de referencia en ₲ (moneda base). Se muestran en la moneda elegida
  // por el visitante (₲ / R$ / US$) según las tasas del backend.
  const { rates, currency } = useCurrency();
  const format = (pyg: number) => formatPrice(pyg, currency, rates);
  // Descuentos vigentes (promo de lanzamiento). El ahorro por ciclo sale de la
  // fórmula (1/5/10 meses pagados); la promo se aplica encima en los 3 ciclos.
  const { data: discounts } = usePricingDiscounts();
  // Ciclo elegido por el visitante (1 mes / 6 meses / 1 año). El número grande es
  // siempre el equivalente mensual; los ciclos largos suman "meses gratis".
  const [cycle, setCycle] = useState<BillingCycle>('monthly');

  // Almacenamiento por plan (marketing): mismo criterio que las cuotas reales —
  // entrada 512 MB, pro 2 GB, personalizado 5 GB.
  const storage = (mb: number) => t('landing.plans.storage', { size: formatStorageMb(mb) });

  const plans: {
    name: string;
    pyg?: number;
    priceText?: string;
    feats: string[];
    cta: string;
    featured?: boolean;
    badge?: string;
    noSuffix?: boolean;
  }[] = [
    {
      name: t('landing.plans.tech.name'),
      pyg: 150_000,
      feats: [t('landing.plans.tech.f1'), t('landing.plans.tech.f2'), t('landing.plans.tech.f3'), storage(512)],
      cta: t('landing.action.try'),
    },
    {
      featured: true,
      badge: t('landing.plans.badge'),
      name: t('landing.plans.mkt.name'),
      // Marketing Pro (plan real del backend).
      pyg: 200_000,
      feats: [t('landing.plans.mkt.f1'), t('landing.plans.mkt.f2'), t('landing.plans.mkt.f3'), storage(2048)],
      cta: t('landing.action.try'),
    },
    {
      name: t('landing.plans.pyme.name'),
      pyg: 120_000,
      feats: [t('landing.plans.pyme.f1'), t('landing.plans.pyme.f2'), t('landing.plans.pyme.f3'), storage(512)],
      cta: t('landing.action.try'),
    },
    {
      name: t('landing.plans.legal.name'),
      // Legal Solo (plan de entrada real del rubro Abogados).
      pyg: 150_000,
      feats: [t('landing.plans.legal.f1'), t('landing.plans.legal.f2'), t('landing.plans.legal.f3'), storage(2048)],
      cta: t('landing.action.try'),
    },
    {
      name: t('landing.plans.rrhh.name'),
      pyg: 180_000,
      feats: [t('landing.plans.rrhh.f1'), t('landing.plans.rrhh.f2'), t('landing.plans.rrhh.f3'), storage(512)],
      cta: t('landing.action.try'),
    },
    {
      name: t('landing.plans.custom.name'),
      priceText: t('landing.plans.custom.price'),
      feats: [t('landing.plans.custom.f1'), t('landing.plans.custom.f2'), t('landing.plans.custom.f3'), storage(5120)],
      cta: t('landing.plans.customCta'),
      noSuffix: true,
    },
  ];

  return (
    <section id="plans" className={`${WRAP} pb-20`}>
      <SectionHead
        center
        eyebrow={t('landing.plans.eyebrow')}
        title={t('landing.plans.title')}
        subtitle={t('landing.plans.subtitle')}
      />
      {/* Selector de ciclo + moneda: viven junto a los precios (no en el header). */}
      <Reveal className="mb-6 flex flex-wrap items-center justify-center gap-3">
        <BillingCycleToggle value={cycle} onChange={setCycle} />
        <CurrencySelector />
      </Reveal>
      <div className="grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((p, i) => {
          // Plan con precio numérico → precio efectivo del ciclo elegido. El plan
          // "Personalizado" usa un texto fijo y no lleva precio calculado.
          const eff = p.pyg !== undefined ? effectivePrice(p.pyg, cycle, discounts) : null;
          const priceNode = eff ? (
            <PriceWithDiscount
              eff={eff}
              cycle={cycle}
              format={format}
              period={p.noSuffix ? undefined : perMonth}
              priceClassName="font-display text-3xl font-extrabold tabular-nums text-ink"
              periodClassName="text-sm font-semibold text-muted"
            />
          ) : (
            <span className="font-display text-3xl font-extrabold tabular-nums text-ink">
              {p.priceText}
            </span>
          );
          // El badge de % es SOLO para la promo de lanzamiento (el ahorro por ciclo
          // se muestra como "meses gratis" dentro de PriceWithDiscount).
          const discountNode =
            eff && discounts?.promo.active ? <DiscountBadge discounts={discounts} /> : undefined;
          return (
            <Reveal key={p.name} delay={(i % 3) * 90} className="h-full">
              <PlanCard
                featured={p.featured}
                badge={p.badge}
                name={p.name}
                priceNode={priceNode}
                discountNode={discountNode}
                features={p.feats}
                cta={p.cta}
                ctaTo="/registro"
              />
            </Reveal>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-muted">{t('landing.plans.footnote')}</p>
    </section>
  );
}

/* ---------------------------------------------------------------- ia (mcp) -- */

/** Sección "Conectá tu IA": diferenciador — operar Tembiapo desde Claude/ChatGPT/Gemini. */
function AiIntegration() {
  return (
    <section id="ia" className={`${WRAP} pb-20`}>
      <Reveal>
        <div className="relative grid items-center gap-8 overflow-hidden rounded-3xl border border-line bg-surface p-8 shadow-card sm:p-10 lg:grid-cols-[1.1fr_1fr]">
          <div
            aria-hidden
            className="pointer-events-none absolute -left-20 -bottom-24 h-72 w-72 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, color-mix(in srgb, var(--brand) 18%, transparent), transparent 70%)',
            }}
          />
          <div className="relative min-w-0">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 font-display text-sm font-extrabold uppercase tracking-wider text-brand">
              <Bot size={16} />
              {t('landing.ai.badge')}
            </span>
            <h2 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">
              {t('landing.ai.title')}
            </h2>
            <p className="mt-3 max-w-[46ch] text-muted">{t('landing.ai.body')}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {['Claude', 'ChatGPT', 'Gemini'].map((s) => (
                <span
                  key={s}
                  className="inline-flex items-center gap-1.5 rounded-token border border-line bg-bg px-3 py-1.5 font-display text-sm font-semibold text-ink"
                >
                  <Sparkles size={14} className="text-brand" />
                  {s}
                </span>
              ))}
            </div>
            <Link
              to="/ayuda/conectar-ia"
              className="mt-5 inline-flex items-center gap-1.5 font-display text-sm font-semibold text-brand hover:text-brand-2"
            >
              {t('landing.ai.cta')}
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Mock de chat: pedido → acción en Tembiapo */}
          <div aria-hidden className="relative min-w-0">
            <div className="rounded-2xl border border-line bg-bg p-4 shadow-lift">
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-danger/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-sun/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-good/70" />
                <span className="ml-2 font-mono text-xs text-muted">tu IA + Tembiapo</span>
              </div>
              <div className="space-y-2.5">
                <div className="ml-auto w-fit max-w-[85%] rounded-2xl rounded-br-sm bg-brand px-3.5 py-2 text-sm text-brand-ink">
                  Creá 3 tareas de onboarding en el proyecto Ventas y asigná la primera a Ana.
                </div>
                <div className="w-fit max-w-[90%] rounded-2xl rounded-bl-sm border border-line bg-surface px-3.5 py-2 text-sm text-ink">
                  <span className="flex items-center gap-1.5 font-medium text-good">
                    <Check size={15} /> Listo
                  </span>
                  <span className="mt-1 block text-muted">
                    Creé 3 tareas en <strong className="text-ink">Ventas</strong> y asigné “Dar de alta al cliente” a <strong className="text-ink">Ana</strong>.
                  </span>
                </div>
              </div>
              <div className="mt-3 overflow-x-auto rounded-token bg-ink/90 px-3 py-2">
                <code className="whitespace-nowrap font-mono text-xs text-bg">
                  claude mcp add tembiapo --transport http …/api/mcp
                </code>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* -------------------------------------------------------------------- apps -- */

function Apps() {
  return (
    <section id="apps" className={`${WRAP} pb-20`}>
      <Reveal>
        <div className="relative grid items-center gap-8 overflow-hidden rounded-3xl border border-line bg-surface p-8 shadow-card sm:p-10 lg:grid-cols-[1.2fr_1fr]">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, color-mix(in srgb, var(--brand) 16%, transparent), transparent 70%)',
            }}
          />
          <div className="relative">
            {/* La app de Android YA está publicada en Google Play. */}
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-good/40 bg-good/15 px-4 py-1.5 font-display text-base font-extrabold uppercase tracking-wider text-good sm:text-lg">
              <Check size={18} />
              {t('landing.apps.live')}
            </span>
            <h2 className="font-display text-2xl font-extrabold text-ink sm:text-3xl">
              {t('landing.apps.title')}
            </h2>
            <p className="mt-3 max-w-[44ch] text-muted">{t('landing.apps.body')}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {/* Botón real a Google Play (app publicada). */}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-token bg-ink px-5 py-3 font-display text-sm font-semibold text-bg shadow-card transition-transform hover:-translate-y-0.5"
              >
                <GooglePlayGlyph />
                {t('landing.apps.androidCta')}
              </a>
              {/* Botón real a la App Store (app publicada en iPhone). */}
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 rounded-token bg-ink px-5 py-3 font-display text-sm font-semibold text-bg shadow-card transition-transform hover:-translate-y-0.5"
              >
                <AppleGlyph />
                {t('landing.apps.iosCta')}
              </a>
              <span className="inline-flex items-center gap-2 rounded-token border border-line bg-surface px-4 py-2.5 font-display text-sm font-semibold text-ink">
                <Smartphone size={16} className="text-brand" />
                PWA
              </span>
            </div>
          </div>
          <div aria-hidden className="relative flex justify-center gap-4">
            <PhoneMock className="tb-float -translate-y-1.5 -rotate-6" icon={<LayoutGrid size={22} />} />
            <PhoneMock
              className="tb-float-slow translate-y-4 rotate-6"
              icon={<Calendar size={22} />}
            />
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/** Marco de dispositivo abstracto (no un screenshot falso): frame teal con un
 *  glifo de app. Comunica "está en el celu" sin fingir una captura de pantalla. */
function PhoneMock({ className, icon }: { className?: string; icon: ReactNode }) {
  return (
    <div
      className={`relative grid h-56 w-32 place-items-center overflow-hidden rounded-[26px] border border-line bg-gradient-to-b from-brand-2 to-brand text-brand-ink shadow-lift ${className ?? ''}`}
    >
      <span
        aria-hidden
        className="absolute left-1/2 top-2 h-1 w-10 -translate-x-1/2 rounded-full bg-brand-ink/40"
      />
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-ink/15 backdrop-blur-sm">
        {icon}
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- cta -- */

function FinalCta() {
  // El gradiente de marca invertía con el tema (teal claro/oscuro) y dejaba el
  // texto blanco ilegible. Lo mantenemos como tarjeta `surface` con texto `ink` /
  // acento `brand`: contraste correcto en claro y oscuro (tokens). El brillo de
  // marca queda en el halo decorativo y en los botones.
  return (
    <section className={`${WRAP} pb-20`}>
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-line bg-surface p-10 text-center shadow-card sm:p-14">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-40"
            style={{
              background:
                'radial-gradient(60% 100% at 50% 0%, color-mix(in srgb, var(--brand) 18%, transparent), transparent 70%)',
            }}
          />
          <div className="relative">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-brand sm:text-4xl">
              {t('landing.final.title')}
            </h2>
            <p className="mx-auto mt-3 max-w-[40ch] text-lg text-ink">{t('landing.final.body')}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/registro"
                onClick={() => trackCta('register')}
                className="group inline-flex items-center gap-2 rounded-token bg-brand px-6 py-3 font-display text-[15px] font-semibold text-brand-ink shadow-card transition-all hover:-translate-y-0.5 hover:bg-brand-2 hover:shadow-card-hover"
              >
                {t('landing.action.try')}
                <ArrowRight
                  size={17}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-token border border-line px-6 py-3 font-display text-[15px] font-semibold text-ink transition-all hover:-translate-y-0.5 hover:border-brand hover:text-brand"
              >
                {t('landing.action.login')}
              </Link>
            </div>
            <p className="mt-5 text-xs text-muted">{t('landing.final.note')}</p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
